import {
  EMPTY_KOMBO,
  T,
  formatCents,
  isComplete,
  komboName,
  komboParts,
  komboProblem,
  komboTotalCents,
  selectablePizzas,
  tt,
  type KomboPizza,
  type KomboSlots,
} from '@agos/shared'
import { cn } from '@agos/ui'
import { Pizza, X } from 'lucide-react'
import { useState } from 'react'

/**
 * Three pizzas in one box, taken over the telephone — `FRONTEND.md` §9.44.
 *
 * Legacy opens `KomboPizzaBuilderModal` from its order builder; the customer app's equivalent was
 * ported and this one was not, so a kombo could be ordered from the website and not by phone.
 *
 * **The arithmetic is `@agos/shared/kombo`, and that is the point of sharing it.** A kombo costs the
 * **dearest** of its three at the kombo's own size — not the sum, which would be roughly three times
 * the offer, and not each pizza's own size, which would sell a 50 cm box at a 32 cm price.
 *
 * Three `<select>`s, as the owner asked — *podobný dropdown ako je v customer stránke*. Similar
 * rather than the same component: the customer's is a bottom sheet in that app's shapes, and this
 * one belongs beside `PizzaExtrasDialog`.
 */
export function KomboPizzaDialog({
  pizzas,
  komboCm,
  onAdd,
  onClose,
}: {
  pizzas: readonly KomboPizza[]
  komboCm: number
  onAdd: (line: {
    name: string
    unitPriceCents: number
    parts?: { productId?: number; name: string; unitPriceCents: number; quantity: number }[]
    /** §10A.26 — always `'kombo'` from here, which is what makes the `max` bound apply. */
    composition?: 'kombo'
  }) => void
  onClose: () => void
}) {
  const [slots, setSlots] = useState<KomboSlots>(EMPTY_KOMBO)
  const options = selectablePizzas(pizzas)
  const total = komboTotalCents(slots, pizzas, komboCm)
  const problem = komboProblem(slots, pizzas)
  const ready = isComplete(slots) && problem === ''

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-kombo-dialog>
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label={tt('Zavrieť')}
        onClick={onClose}
      />
      <section className="relative w-full max-w-md rounded-2xl bg-background border border-border overflow-hidden">
        <header className="px-5 pt-4 pb-3 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Pizza className="w-4 h-4 text-primary" />
              <T>Kombo pizza</T>
            </p>
            <h2 className="text-base font-bold leading-tight">
              {komboCm} cm · <T>tri polovice</T>
            </h2>
          </div>
          <button
            type="button"
            aria-label={tt('Zavrieť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </header>

        <div className="p-4 space-y-2">
          {slots.map((slot, index) => (
            <label key={index} className="block space-y-1">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                {index + 1}. <T>pizza</T>
              </span>
              <select
                data-kombo-slot={index}
                value={slot === null ? '' : String(slot)}
                onChange={(event) => {
                  const next = [...slots]
                  next[index] = event.target.value === '' ? null : Number(event.target.value)
                  setSlots(next)
                }}
                aria-label={`${index + 1}. ${tt('pizza')}`}
                className="w-full min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
              >
                <option value="">{tt('Vyberte pizzu')}</option>
                {options.map((pizza) => (
                  <option key={pizza.id} value={pizza.id}>
                    {pizza.name}
                  </option>
                ))}
              </select>
            </label>
          ))}

          {problem !== '' && (
            <p className="text-[11px] text-amber-500" data-kombo-problem>
              {problem}
            </p>
          )}
        </div>

        <footer className="p-4 border-t border-border space-y-2">
          {/* The dearest of the three, not the sum - `komboTotalCents`, and the reason it is shared. */}
          <p className="text-xs text-muted-foreground" data-kombo-total>
            {formatCents(total)}
          </p>
          <button
            type="button"
            data-kombo-add
            disabled={!ready}
            onClick={() => {
              // §10A.14 — the same three parts the customer app sends, from the same helper, so a
              // kombo means one thing whether it was ordered on the website or by telephone.
              //
              // §10A.26 — and the same `composition`, for the same reason. This is the payload that
              // once sent a telephoned pizza at nought, so the two agreeing here is the point.
              onAdd({
                name: komboName(slots, pizzas),
                unitPriceCents: total,
                parts: komboParts(slots, pizzas, komboCm),
                composition: 'kombo',
              })
              setSlots(EMPTY_KOMBO)
            }}
            className={cn(
              'w-full min-h-[48px] rounded-xl bg-primary text-primary-foreground font-bold transition-opacity',
              ready ? 'hover:opacity-90' : 'opacity-50',
            )}
          >
            <T>Pridať kombo do košíka</T>
          </button>
        </footer>
      </section>
    </div>
  )
}
